import { useCallback, useEffect, useRef, useState } from "react";
import TspApiService from "../../../api/tspApiService";
import { appendToSessionLines } from "./trackingModel";

// Live tracking data of one journey for the screens, by lightweight polling (the
// backend has no WebSocket/SSE infrastructure, and a few-second delay is fine
// for a walking/driving party):
//   - status (who is tracking, their latest position and health) every `statusMs`
//   - path: loaded once in full (paged by cursor), then only NEW points (after
//     the last id seen) on each poll — nothing is re-downloaded or redrawn.
// Polling pauses while the tab is hidden and catches up when it is shown again.
//
// `withPath: false` (journey page) polls the status only.
const PATH_PAGE = 5000;
const MAX_INITIAL_PAGES = 20; // 100k points: far beyond one journey; guards a runaway loop

export default function useTspJourneyTracking(journeyId, { statusMs = 5000, withPath = true } = {}) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [lines, setLines] = useState(() => new Map());
  const [pathLoaded, setPathLoaded] = useState(!withPath);
  const [lastPoints, setLastPoints] = useState([]); // the points added by the latest poll
  const cursor = useRef(0);
  const inFlight = useRef(false);
  const alive = useRef(true);

  const loadPath = useCallback(
    async (initial) => {
      let pages = 0;
      let added = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const response = await TspApiService.tracking.getPath(journeyId, { after: cursor.current, limit: PATH_PAGE });
        if (!alive.current) return;
        const points = response.data || [];
        if (points.length) {
          cursor.current = response.meta.next_after;
          added = added.concat(points);
        }
        pages += 1;
        if (!response.meta?.has_more || pages >= (initial ? MAX_INITIAL_PAGES : 2)) break;
      }
      if (added.length) {
        setLines((previous) => appendToSessionLines(previous, added));
        setLastPoints(added);
      }
    },
    [journeyId]
  );

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const response = await TspApiService.tracking.getStatus(journeyId);
      if (!alive.current) return;
      setStatus(response.data);
      setError(null);
      if (withPath) await loadPath(false);
    } catch (err) {
      if (alive.current) setError(err); // keep the last good data on screen
    } finally {
      inFlight.current = false;
    }
  }, [journeyId, withPath, loadPath]);

  // First load: status + the whole path.
  useEffect(() => {
    alive.current = true;
    cursor.current = 0;
    setStatus(null);
    setError(null);
    setLines(new Map());
    setPathLoaded(!withPath);
    (async () => {
      inFlight.current = true;
      try {
        const response = await TspApiService.tracking.getStatus(journeyId);
        if (!alive.current) return;
        setStatus(response.data);
        if (withPath) {
          await loadPath(true);
          if (alive.current) setPathLoaded(true);
        }
      } catch (err) {
        if (alive.current) setError(err);
      } finally {
        inFlight.current = false;
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [journeyId, withPath, loadPath]);

  // Polling while visible.
  useEffect(() => {
    if (!statusMs) return undefined;
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      refresh();
    };
    const timer = setInterval(tick, statusMs);
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [statusMs, refresh]);

  return { status, error, lines, pathLoaded, lastPoints, refresh };
}
